import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/top-bar";
import StickyActionButton from "@/components/sticky-action-button";
import {
  useRequestOtp,
  useVerifyOtp,
  type OtpRequestError,
  type OtpVerifyError,
} from "@/features/auth/hooks/use-otp-login";

// Code length is server-configurable (currently 4 — see docs/web-otp-auth.md).
const OTP_LENGTH = 4;

function toLatinDigits(value: string, max = Infinity) {
  return Array.from(value)
    .map((c) => {
      const code = c.charCodeAt(0);
      if (code >= 1776 && code <= 1785) return String(code - 1776);
      if (code >= 1632 && code <= 1641) return String(code - 1632);
      return c;
    })
    .join("")
    .replace(/\D/g, "")
    .slice(0, max);
}

// The user types what they want; the backend normalizes. We only sanity-check
// the common "09xxxxxxxxx" shape before enabling the send button.
const isValidIranianMobile = (value: string) => /^09\d{9}$/.test(value);

type Step = "phone" | "otp";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where to return after login. Only accept internal paths to avoid open
  // redirects (must start with a single "/").
  const rawRedirect = searchParams.get("redirect");
  const redirectTo =
    rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
      ? rawRedirect
      : null;
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(0);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();

  const phoneValid = isValidIranianMobile(phone);
  const otpValue = otp.join("");
  const otpValid = otpValue.length === OTP_LENGTH;

  useEffect(() => {
    if (step !== "otp" || secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step === "otp") otpRefs.current[0]?.focus();
  }, [step]);

  // Step 1/3 — request a code. The backend response is generic; the returned
  // value is the resend cooldown (seconds) to keep "ارسال مجدد" disabled.
  const sendCode = async (isResend: boolean) => {
    if (!phoneValid || requestOtp.isPending) return;
    try {
      const cooldown = await requestOtp.mutateAsync(phone);
      setOtp(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(cooldown);
      setStep("otp");
      toast.success(isResend ? "کد تایید مجدداً ارسال شد." : "کد تایید ارسال شد.");
    } catch (e) {
      const err = e as OtpRequestError;
      // On a cooldown the backend tells us how long to wait — honor it.
      if (err.kind === "cooldown" && err.retryAfter) {
        setSecondsLeft(err.retryAfter);
      }
      toast.error(err.message);
    }
  };

  const handleSendCode = () => void sendCode(false);

  const handleResend = () => {
    if (secondsLeft > 0 || requestOtp.isPending) return;
    void sendCode(true);
  };

  // Step 2/3 — verify. On success the hook persists tokens + profile; we just
  // route. On a burned/expired code (`reset`) bounce back to the phone step.
  const runVerify = async (code: string) => {
    if (code.length !== OTP_LENGTH || verifyOtp.isPending) return;
    try {
      const user = await verifyOtp.mutateAsync({ phone, code });
      toast.success("ورود با موفقیت انجام شد.");
      // Incomplete profiles must finish onboarding first; otherwise return to
      // the page that sent the user here, falling back to the profile.
      const destination = redirectTo ? redirectTo : '/profile'
        // user.profile_completed === false
        //   ? "/profile"
        //   : redirectTo || "/profile";

      navigate(destination, { replace: true });
    } catch (e) {
      const err = e as OtpVerifyError;
      toast.error(err.message);
      setOtp(Array(OTP_LENGTH).fill(""));
      if (err.reset) {
        setStep("phone");
      } else {
        otpRefs.current[0]?.focus();
      }
    }
  };

  const handleVerify = () => void runVerify(otpValue);

  const handleOtpChange = (index: number, raw: string) => {
    const digits = toLatinDigits(raw);
    if (!digits) {
      const next = [...otp];
      next[index] = "";
      setOtp(next);
      return;
    }
    // Support paste / SMS-autofill of the full code into any box.
    if (digits.length > 1) {
      const next = Array(OTP_LENGTH).fill("");
      for (let i = 0; i < OTP_LENGTH && i < digits.length; i++) {
        next[i] = digits[i];
      }
      setOtp(next);
      const lastFilled = Math.min(digits.length, OTP_LENGTH) - 1;
      otpRefs.current[Math.min(lastFilled + 1, OTP_LENGTH - 1)]?.focus();
      if (next.join("").length === OTP_LENGTH) void runVerify(next.join(""));
      return;
    }
    const next = [...otp];
    next[index] = digits;
    setOtp(next);
    if (index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus();
    // Auto-submit once every box is filled.
    if (next.join("").length === OTP_LENGTH) void runVerify(next.join(""));
  };

  const handleOtpKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="min-h-svh bg-secondary-background">
      <TopBar
        title="ورود به حساب"
        onBackClick={() => {
          if (step === "otp") {
            setStep("phone");
            return;
          }
          navigate(-1);
        }}
      />

      <div className="px-5 pt-8 pb-28 max-w-md mx-auto">
        <div className="mb-8 flex flex-col items-center text-center gap-3">
          <h1 className="text-base font-bold text-default-900">
            {step === "phone" ? "ورود یا ثبت‌نام" : "تایید شماره موبایل"}
          </h1>
          <p className="text-xs text-default-500 leading-6">
            {step === "phone"
              ? "شماره موبایل خود را وارد کنید تا کد تایید برایتان ارسال شود."
              : `کد ${OTP_LENGTH.toLocaleString("fa-IR")} رقمی ارسال شده به شماره ${phone} را وارد کنید.`}
          </p>
        </div>

        {step === "phone" ? (
          <div className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold text-default-700 mb-1.5">
                شماره موبایل
              </span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                dir="ltr"
                placeholder="09xxxxxxxxx"
                value={phone}
                onChange={(e) => setPhone(toLatinDigits(e.target.value, 11))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSendCode();
                }}
                className="w-full h-12 rounded-xl border border-default-100 bg-background px-4 text-sm text-default-800 placeholder:text-default-400 tracking-wider focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70"
              />
              {phone && !phoneValid ? (
                <span className="block mt-1.5 text-xs text-danger">
                  شماره موبایل نامعتبر است.
                </span>
              ) : null}
            </label>

            <p className="text-[11px] leading-6 text-default-500 text-center pt-2">
              با ورود، با{" "}
              <span className="text-primary">قوانین و مقررات</span> آموزیم موافقت
              می‌کنید.
            </p>

            <StickyActionButton
              label={requestOtp.isPending ? "در حال ارسال..." : "ارسال کد تایید"}
              onClick={handleSendCode}
              disabled={!phoneValid || requestOtp.isPending}
            />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex justify-center gap-2" dir="ltr">
              {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_LENGTH}
                  value={otp[i]}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  disabled={verifyOtp.isPending}
                  className="size-12 text-center text-lg font-bold rounded-xl border border-default-100 bg-background text-default-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70 disabled:opacity-60"
                />
              ))}
            </div>

            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => setStep("phone")}
                className="text-default-600 font-medium"
              >
                ویرایش شماره
              </button>
              {secondsLeft > 0 ? (
                <span className="text-default-500">
                  ارسال مجدد تا {secondsLeft.toLocaleString("fa-IR")} ثانیه
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={requestOtp.isPending}
                  className="text-primary font-semibold disabled:opacity-60"
                >
                  ارسال مجدد کد
                </button>
              )}
            </div>

            <StickyActionButton
              label={verifyOtp.isPending ? "در حال بررسی..." : "تایید و ورود"}
              onClick={handleVerify}
              disabled={!otpValid || verifyOtp.isPending}
            />
          </div>
        )}
      </div>
    </div>
  );
}
